import { t } from '@lingui/core/macro';
import {
  Avatar,
  Box,
  Divider,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import IconButtonAnimate from 'components/animate/IconButtonAnimate';
import NavMenuItem from 'components/buttons/NavMenuItem';
import { CheckIcon } from 'components/icons/CheckIcon';
import { UnfoldMoreIconMUI } from 'components/icons/UnfoldMoreIconMUI';
import MenuPopover from 'components/MenuPopover';
import {
  useCurrentResource,
  useCurrentRole,
  useCurrentUser,
  useIsCurrentUserAdmin,
  useIsUserValidator,
} from 'hooks/slices';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import NavbarCard from 'layouts/dashboard/navbar/NavbarCard';
import { useState } from 'react';
import { useAuthStore } from 'stores/authStore';
import type { ResourceType, RoleWithResource } from 'types/models';
import {
  DIVIDERS_HEIGHT,
  LOGOUT_HEIGHT,
  NAVBAR_PADDING_Y,
  POPOVER_HEIGHT,
  PROFILE_NAVCARD_HEIGHT,
  USER_NAVCARD_HEIGHT,
} from 'utils/accountPopoverUtils';
import { truthy } from 'utils/arrayUtils';
import { useEnvironmentIndicatorStore } from 'utils/environmentIndicator';
import { formatPhoneNumberWithCountryCode } from 'utils/phoneUtils';
import { isFaksRole, isResourceScopedRole } from 'utils/rolesUtils';
import { userUtils } from 'utils/userUtils';

const POPOVER_RESERVED_HEIGHT =
  NAVBAR_PADDING_Y +
  POPOVER_HEIGHT +
  DIVIDERS_HEIGHT +
  USER_NAVCARD_HEIGHT +
  PROFILE_NAVCARD_HEIGHT +
  LOGOUT_HEIGHT;

type AccountTriggerProps = {
  currentUser: ReturnType<typeof useCurrentUser>;
  currentResource: ReturnType<typeof useCurrentResource>;
};

const AccountAvatar = ({
  currentUser,
  avatarSize,
}: {
  currentUser: ReturnType<typeof useCurrentUser>;
  avatarSize?: number;
}) => (
  <Avatar
    src={currentUser?.profile_picture_url || ''}
    alt={currentUser?.first_name || ''}
    sx={avatarSize ? { width: avatarSize, height: avatarSize } : undefined}
  />
);

const CollapsedAccountTrigger = ({ currentUser }: AccountTriggerProps) => (
  <AccountAvatar currentUser={currentUser} avatarSize={36} />
);

const ExpandedAccountTrigger = ({ currentUser, currentResource }: AccountTriggerProps) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <AccountAvatar currentUser={currentUser} />
        <Box>
          <Typography variant="subtitle2">{currentResource?.name}</Typography>
          <Typography color={theme.palette.navBar.menuSecondaryText}>
            {userUtils.getFullName(currentUser)}
          </Typography>
        </Box>
      </Stack>
      <UnfoldMoreIconMUI
        data-testid="OpenAccountPopover"
        fontSize="large"
        sx={{ color: theme.palette.text.secondary }}
      />
    </Stack>
  );
};

export default function AccountPopover() {
  const { isCollapse } = useCollapseDrawer();
  const theme = useTheme();
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isUserAdmin = useIsCurrentUserAdmin();
  const isUserValidator = useIsUserValidator();
  const currentRole = useCurrentRole();
  const currentResource = useCurrentResource();
  const signOut = useAuthStore((state) => state.signOut);
  const environmentIndicatorState = useEnvironmentIndicatorStore();

  const getResourceType = (resourceType: ResourceType) => {
    switch (resourceType) {
      case 'Group':
        return t`Groupement`;
      case 'Lab':
        return t`Laboratoire`;
      case 'Pharmacy':
        return t`Pharmacie`;
      default:
        return '';
    }
  };

  const resourceRoles = (currentUser?.roles || [])
    .filter(isResourceScopedRole)
    .sort((a, b) => (a.resource.name || '').localeCompare(b.resource.name || ''));
  // The Faks org role is switchable too — one entry giving access to the back-office.
  const faksRole = (currentUser?.roles || []).find(isFaksRole);
  const roles = faksRole ? [faksRole, ...resourceRoles] : resourceRoles;

  const isPharmacy = currentRole && currentRole.resource_type === 'Pharmacy';
  const isGroupAdmin =
    currentRole && currentRole.resource_type === 'Group' && currentRole.kind === 'group_admin';

  const menuItems = [
    currentRole && !isPharmacy && { label: t`Mon profil`, to: '/user/profile' as const },
    isGroupAdmin && {
      label: t`Paramètres`,
      to: `/group/$id/edit` as const,
      params: { id: currentRole.resource_id },
    },
  ].filter(truthy);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const setCurrentRoleId = useAuthStore((state) => state.setCurrentRoleId);

  const handleSwitchRole = (roleItem: RoleWithResource) => {
    if (roleItem.id === currentRole?.id) return;
    setCurrentRoleId(roleItem.id);
    navigate({ to: '/' });
  };

  return (
    <>
      <Tooltip title={isCollapse ? (currentResource?.name ?? '') : ''} placement="right">
        <IconButtonAnimate
          onClick={handleOpen}
          sx={{
            p: isCollapse ? 1 : 2,
            width: '100%',
            color: theme.palette.navBar.menuDefaultText,
            textAlign: 'initial',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: theme.palette.navBar.menuHoverBg,
              color: theme.palette.navBar.menuHoverText,
            },
            ...(Boolean(open) && {
              bgcolor: theme.palette.navBar.menuHoverBg,
              color: theme.palette.navBar.menuHoverText,
            }),
          }}
        >
          {isCollapse ? (
            <CollapsedAccountTrigger currentUser={currentUser} currentResource={currentResource} />
          ) : (
            <ExpandedAccountTrigger currentUser={currentUser} currentResource={currentResource} />
          )}
        </IconButtonAnimate>
      </Tooltip>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        arrow="bottom-center"
        sx={{ p: 0, minWidth: '247px' }}
      >
        {currentUser && (
          <NavbarCard
            title={`${currentUser.first_name} ${currentUser.last_name}` || ''}
            subtitle={
              currentUser.verified_phone
                ? formatPhoneNumberWithCountryCode(currentUser.verified_phone)
                : ''
            }
          />
        )}
        {currentRole || isUserAdmin || isUserValidator ? (
          <>
            {menuItems.length > 0 && (
              <>
                <Divider />
                <Stack sx={{ p: 1 }}>
                  {menuItems.map(({ label, ...linkProps }) => (
                    <MenuItem
                      key={label}
                      onClick={() => {
                        handleClose();
                        navigate(linkProps);
                      }}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </Stack>
              </>
            )}

            <Divider />

            <Stack
              sx={{ maxHeight: `calc(100vh - ${POPOVER_RESERVED_HEIGHT}px)`, overflowY: 'scroll' }}
            >
              {roles.map((role) => (
                <MenuItem
                  key={`${role.id}_${role.resource.id}`}
                  sx={{ m: 1, justifyContent: 'space-between' }}
                  onClick={() => handleSwitchRole(role)}
                >
                  <Box>
                    <Typography variant="subtitle2">{role.resource.name}</Typography>
                    <Typography color={theme.palette.text.secondary}>
                      {role.resource_type === 'Core::Resource'
                        ? t`Back-office`
                        : `${t`Espace`} ${getResourceType(role.resource_type).toLowerCase()}`}
                    </Typography>
                  </Box>
                  {role.id === currentRole?.id && (
                    <CheckIcon style={{ fill: theme.palette.success.main }} />
                  )}
                </MenuItem>
              ))}
            </Stack>

            <Divider />

            <MenuItem sx={{ m: 1 }} onClick={() => signOut({ userInitiated: true })}>
              {t`Se déconnecter`}
            </MenuItem>

            {environmentIndicatorState.shouldShow() && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    environmentIndicatorState.hideForFiveMinutes();
                    handleClose();
                  }}
                >
                  <Typography color={theme.palette.text.secondary}>
                    {t`Cacher l’environnement (5min)`}
                  </Typography>
                </MenuItem>
              </>
            )}
          </>
        ) : (
          <NavMenuItem to="/login/phone_number" sx={{ m: 1 }}>
            {t`Se connecter`}
          </NavMenuItem>
        )}
      </MenuPopover>
    </>
  );
}
